<script setup lang="ts">
import { hasPermission } from '@permission/hasPermission';
import { useErrorHandler } from '@errors/useErrorHandler';
import EditButton from '@relatie-lib/EditButton.vue';
import { useRelatieContactpersonenVerwijderRelatieContactPersoon } from '@services/crm-public/orval-api/relatie-contactpersonen-api/relatie-contactpersonen-api';
import ExpandTransition from '@shared/animation/transitions/ExpandTransition/ExpandTransition.vue';
import { VButton } from '@shared/component/VButton';
import VButtonGroup from '@shared/component/VButtonGroup/VButtonGroup.vue';
import VModal from '@shared/component/VModal/VModal.vue';
import VTooltip from '@shared/component/VTooltip/VTooltip.vue';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { useConfirmDialog } from '@vueuse/core';
import { ref, toRefs } from 'vue';
import FormLayout from '../FormLayout.vue';
import ContactpersoonForm from './ContactpersoonForm.vue';
import { type ContactpersoonEditMode } from './types';

const props = defineProps<{ relatienummer: number }>();

const { relatienummer } = toRefs(props);

const magBijwerken = hasPermission('Crm.RelatieBijwerken');

const notification = useNotification();
const { handleError } = useErrorHandler();

const contactpersonen = defineModel<Array<ContactpersoonEditMode>>('modelValue', {
  required: true,
});

const forms = ref<Array<InstanceType<typeof ContactpersoonForm>>>([]);

const { reveal, isRevealed, confirm, cancel } = useConfirmDialog();

function push() {
  contactpersonen.value.push({
    id: '',
    titulatuurcode: undefined,
    naam: '',
    voorletters: '',
    voorvoegsels: '',
    voornaam: '',
    geboortedatum: undefined,
    email: '',
    telefoonnummer: null,
    inEditMode: true,
    isLoading: false,
  });
}

function onError(err: any) {
  handleError(err);
}

const { mutate } = useRelatieContactpersonenVerwijderRelatieContactPersoon({
  mutation: {
    onSuccess: (_, { contactpersoonId }) => {
      notification.success('Contactpersoon verwijderd.');
      const idx = contactpersonen.value.findIndex((c) => c.id === contactpersoonId);
      contactpersonen.value.splice(idx, 1);
    },
    onError,
  },
});

async function remove(index: number) {
  const { isCanceled } = await reveal();
  if (isCanceled) return;

  const contactpersoonId = contactpersonen.value[index].id;
  if (!contactpersoonId) {
    contactpersonen.value.splice(index, 1);
    return;
  }
  mutate({ relatienummer: relatienummer.value, contactpersoonId });
}

function submit(index: number) {
  forms.value[index].save();
}

function getTitle(index: number) {
  const naam = 'Contactpersoon';
  const nummerTonen = contactpersonen.value.length > 1;
  return nummerTonen ? `${naam} #${index + 1}` : naam;
}
</script>
<template>
  <ExpandTransition group>
    <FormLayout
      v-for="(contactpersoon, index) in contactpersonen"
      class="duration-400!"
      :key="contactpersoon.id"
      :kan-bewerken="
        (contactpersoon.inEditMode || contactpersonen.filter((c) => c.inEditMode).length === 0) &&
        !!contactpersoon.id
      "
      :title="getTitle(index)"
      v-model="contactpersoon.inEditMode"
      @save="submit(index)"
      :is-submitting="contactpersoon.isLoading"
      kan-opslaan
    >
      <template #header>
        <VTooltip label="Contactpersoon verwijderen" v-if="contactpersonen.length > 1">
          <EditButton
            @click="remove(index)"
            title="Verwijderen"
            icon="fas fa-trash"
            v-if="contactpersoon.inEditMode"
          />
        </VTooltip>
      </template>
      <ContactpersoonForm
        ref="forms"
        :relatienummer
        :is-readonly="!contactpersoon.inEditMode"
        v-model="contactpersonen[index]"
      />
    </FormLayout>
  </ExpandTransition>
  <VButtonGroup align="right" v-if="magBijwerken">
    <VButton
      :disabled="contactpersonen.filter((c) => c.inEditMode).length > 0"
      @click="push()"
      inverted
      prepend-icon="far fa-plus"
    >
      Contactpersoon toevoegen
    </VButton>
  </VButtonGroup>
  <VModal
    :is-active="isRevealed"
    @close-modal="cancel()"
    @cancel-button-clicked="cancel()"
    deny-button-text="Verwijderen"
    confirm-button-text=""
    @deny-action="confirm()"
  >
    Weet je zeker dat je deze contactpersoon wilt verwijderen?
  </VModal>
</template>
